import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Search, Zap, Shield, PackageSearch, CreditCard } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const ShippingQuoterPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    origin_cp: "", destination_cp: "", weight: "", length: "30", width: "20", height: "15", declared_value: ""
  });
  const [originInfo, setOriginInfo] = useState(null);
  const [destInfo, setDestInfo] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [purchasingQuote, setPurchasingQuote] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try { const response = await axios.get(`${API}/stats/public`); setStats(response.data); } catch (error) { /* error handled */; }
    };
    fetchStats();
  }, []);

  const lookupPostalCode = async (cp, type) => {
    if (cp.length === 5) {
      try {
        const response = await axios.get(`${API}/postal-codes/${cp}`);
        if (type === "origin") setOriginInfo(response.data.data);
        else setDestInfo(response.data.data);
      } catch (error) { /* error handled */; }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [quotesRes, insuranceRes] = await Promise.all([
        axios.post(`${API}/shipping/compare`, null, {
          params: { origin_cp: formData.origin_cp, destination_cp: formData.destination_cp, weight: parseFloat(formData.weight), length: parseFloat(formData.length), width: parseFloat(formData.width), height: parseFloat(formData.height) }
        }),
        formData.declared_value ? axios.post(`${API}/shipping/insurance/calculate`, null, { params: { declared_value: parseFloat(formData.declared_value) } }) : Promise.resolve({ data: { options: [] } })
      ]);
      setQuotes(quotesRes.data.quotes);
      setInsuranceOptions(insuranceRes.data.options || []);
      toast.success(`¡${quotesRes.data.total_options} opciones encontradas!`);
    } catch (error) { toast.error("Error al comparar tarifas."); } finally { setLoading(false); }
  };

  const handleBuyQuote = async (quote) => {
    if (!user) { toast.error("Inicia sesión para comprar"); return; }
    setPurchasingQuote(quote);
    try {
      const token = sessionStorage.getItem("token");
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/shipping`, {
        origin_url: originUrl,
        carrier_id: quote.carrier.id,
        service_type: quote.service_type,
        origin_cp: formData.origin_cp,
        destination_cp: formData.destination_cp,
        weight: parseFloat(formData.weight),
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        insurance_type: selectedInsurance?.type || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar pago");
    } finally { setPurchasingQuote(null); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {stats && (
          <div className="bg-[#2E7D32] text-white rounded-2xl p-6 mb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center"><p className="text-3xl font-black">+{(stats.shipments_total / 1000000).toFixed(0)}M</p><p className="text-xs text-white/70">Envíos realizados</p></div>
            <div className="text-center"><p className="text-3xl font-black">+{(stats.clients_total / 1000).toFixed(0)}K</p><p className="text-xs text-white/70">Clientes activos</p></div>
            <div className="text-center"><p className="text-3xl font-black">{stats.years_experience}+</p><p className="text-xs text-white/70">Años</p></div>
            <div className="text-center"><p className="text-3xl font-black">{stats.satisfaction_rate}%</p><p className="text-xs text-white/70">Satisfacción</p></div>
          </div>
        )}

        <div className="text-center mb-8">
          <img src="https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/4hjy2fmk_30FFF72E-C679-49F2-BF56-21BE3B68074B.webp" alt="ZipNGo Fulfillment" className="h-16 w-auto object-contain mx-auto mb-4" />
          <span className="inline-flex items-center gap-2 bg-[#F57C00] text-white px-4 py-2 rounded-full text-sm font-bold mb-4"><Zap size={16} />50% OFF en tu primer envío</span>
          <h1 className="text-3xl md:text-4xl font-black text-[#5D4037]">Compara tarifas de todas las paqueterías</h1>
          <p className="text-stone-600 mt-4">DHL - FedEx - UPS - Estafeta - Redpack - Paquetexpress</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card p-6 sticky top-28">
              <h2 className="font-bold text-lg text-[#5D4037] mb-6">Datos del envío</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#5D4037] mb-2">CP Origen *</label>
                  <input type="text" data-testid="quote-origin-cp" value={formData.origin_cp} onChange={(e) => { const cp = e.target.value.replace(/\D/g, '').slice(0, 5); setFormData({...formData, origin_cp: cp}); lookupPostalCode(cp, "origin"); }} placeholder="Ej: 06600" maxLength={5} className="w-full h-12 px-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none font-mono" required />
                  {originInfo && <p className="text-xs text-[#2E7D32] mt-1">{originInfo.colonia}, {originInfo.ciudad}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#5D4037] mb-2">CP Destino *</label>
                  <input type="text" data-testid="quote-dest-cp" value={formData.destination_cp} onChange={(e) => { const cp = e.target.value.replace(/\D/g, '').slice(0, 5); setFormData({...formData, destination_cp: cp}); lookupPostalCode(cp, "dest"); }} placeholder="Ej: 44100" maxLength={5} className="w-full h-12 px-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none font-mono" required />
                  {destInfo && <p className="text-xs text-[#2E7D32] mt-1">{destInfo.colonia}, {destInfo.ciudad}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#5D4037] mb-2">Peso (kg) *</label>
                  <input type="number" data-testid="quote-weight" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} placeholder="Ej: 2.5" step="0.1" min="0.1" className="w-full h-12 px-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#5D4037] mb-2">Dimensiones (cm)</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" value={formData.length} onChange={(e) => setFormData({...formData, length: e.target.value})} placeholder="Largo" className="h-12 px-3 rounded-xl border border-stone-200 focus:border-[#2E7D32] outline-none text-sm" />
                    <input type="number" value={formData.width} onChange={(e) => setFormData({...formData, width: e.target.value})} placeholder="Ancho" className="h-12 px-3 rounded-xl border border-stone-200 focus:border-[#2E7D32] outline-none text-sm" />
                    <input type="number" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} placeholder="Alto" className="h-12 px-3 rounded-xl border border-stone-200 focus:border-[#2E7D32] outline-none text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#5D4037] mb-2">Valor declarado (MXN) <span className="text-stone-400 font-normal ml-1">opcional</span></label>
                  <input type="number" data-testid="quote-value" value={formData.declared_value} onChange={(e) => setFormData({...formData, declared_value: e.target.value})} placeholder="Para calcular seguro" className="w-full h-12 px-4 rounded-xl border border-stone-200 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none" />
                </div>
                <button type="submit" data-testid="quote-compare-btn" disabled={loading} className="w-full btn-primary h-14 flex items-center justify-center gap-2 text-lg">
                  {loading ? "Comparando..." : <><Search size={20} /> Comparar Tarifas</>}
                </button>
              </form>
            </motion.div>
          </div>

          <div className="lg:col-span-2">
            {quotes.length === 0 ? (
              <div className="bento-card p-12 text-center">
                <Truck size={64} className="text-stone-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#5D4037] mb-2">Ingresa los datos de tu envío</h3>
                <p className="text-stone-500">Compararemos las tarifas de 6 paqueterías en segundos</p>
              </div>
            ) : (
              <div className="space-y-4">
                {insuranceOptions.length > 0 && (
                  <div className="bento-card p-6 bg-gradient-to-r from-[#F57C00]/10 to-[#FF9800]/10 border-[#F57C00]/20">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield size={20} className="text-[#F57C00]" />
                      <h3 className="font-bold text-[#5D4037]">Protección SOS</h3>
                      <span className="text-xs bg-[#F57C00] text-white px-2 py-0.5 rounded-full">Recomendado</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {insuranceOptions.map((option, i) => (
                        <button key={i} onClick={() => setSelectedInsurance(option)} className={`p-3 rounded-xl border-2 transition-all text-left ${selectedInsurance?.type === option.type ? "border-[#F57C00] bg-white" : "border-transparent bg-white/50 hover:bg-white"}`}>
                          <p className="font-bold text-sm text-[#5D4037]">{option.name}</p>
                          <p className="text-xs text-stone-500">Hasta ${option.max_coverage.toLocaleString()}</p>
                          <p className="text-lg font-black text-[#F57C00] mt-1">${option.cost}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <h3 className="font-bold text-lg text-[#5D4037]">{quotes.length} opciones encontradas</h3>
                {quotes.slice(0, 12).map((quote, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={`bento-card p-5 flex items-center gap-4 ${i === 0 ? "ring-2 ring-[#4CAF50] bg-[#4CAF50]/5" : ""}`} data-testid={`quote-result-${i}`}>
                    {i === 0 && <span className="absolute -top-2 left-4 bg-[#4CAF50] text-white text-xs font-bold px-2 py-0.5 rounded-full">Mejor precio</span>}
                    <div className="w-16 h-12 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-stone-600">{quote.carrier.name.split(' ')[0]}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-[#5D4037]">{quote.carrier.name}</p>
                      <p className="text-sm text-stone-500 capitalize">{quote.service_type} - {quote.estimated_days}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-[#2E7D32]">${quote.price.toFixed(2)}</p>
                      <p className="text-xs text-stone-400">MXN + IVA</p>
                    </div>
                    <button onClick={() => handleBuyQuote(quote)} disabled={purchasingQuote === quote} className="btn-primary py-2 px-4 text-sm flex items-center gap-1" data-testid={`buy-quote-${i}`}>
                      <CreditCard size={14} />
                      {purchasingQuote === quote ? "..." : "Comprar"}
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-stone-600 mb-4">¿Ya tienes un envío?</p>
          <Link to="/rastreo" className="btn-secondary inline-flex items-center gap-2" data-testid="quote-track-link">
            <PackageSearch size={20} /> Rastrear Envío
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShippingQuoterPage;
